/**
 * One onboarding run item (plan docs/plans/lms-plan.md 12.3; WP-47; R14):
 * its title, state, kind and due date, the description, the link, who is
 * responsible, what evidence it asks for, and, once worked, the evidence, the
 * note or the reason, and the verification. The hire's tab and the admin's
 * run page render the same card with their own actions (12.3 "the admin's
 * view: every item with the same controls").
 *
 * The responsible rule (12.3): the hire reads "You" on their own items and
 * "Waiting on an admin" on an admin's open item (read-only for them; the
 * item carries no admin's name, so none is shown); an admin reads "An admin"
 * or the hire's name.
 */
import type { ReactNode } from 'react';
import { ExternalLink, Loader2, Paperclip } from 'lucide-react';
import MdLite from '../../content/MdLite';
import type { OnboardingRunItem } from '../../api/onboarding';
import { EvidenceBlockedLink } from './useEvidenceViewer';
import { dueWords, EVIDENCE_LABELS, formatDay, hostOf, itemStateOf, ItemStatePill, KindPill, safeHref } from './shared';

export interface ItemCardProps {
  item: OnboardingRunItem;
  /** YYYY-MM-DD, for the overdue state. */
  today: string;
  /** Who reads the card (the responsible rule of 12.3). */
  viewer: 'hire' | 'admin';
  /** The hire's name, for an admin reading a new-hire item. */
  hireName?: string;
  /** The buttons for this reader (Mark done, Not applicable, Verify, Note, the course link). */
  actions?: ReactNode;
  /** Opens the evidence file; the button shows only when the item has one. */
  onViewEvidence?: () => void;
  viewing?: boolean;
  /** A presigned URL the popup blocker stopped (18.27), shown as a link. */
  blockedUrl?: string | null;
}

const metaCls = 'text-xs text-gray-600 dark:text-gray-400';

export function responsibleWords(item: OnboardingRunItem, viewer: 'hire' | 'admin', hireName?: string): string {
  if (viewer === 'hire') {
    if (item.responsible === 'new_hire') return 'You';
    return item.status === 'pending' ? 'Waiting on an admin' : 'An admin';
  }
  if (item.responsible === 'admin') return 'An admin';
  return hireName || 'The new team member';
}

export function ItemCard({ item, today, viewer, hireName, actions, onViewEvidence, viewing = false, blockedUrl = null }: ItemCardProps) {
  const state = itemStateOf(item, today);
  const href = safeHref(item.url);
  const evidenceText = (item.evidence_text ?? '').trim();
  const note = (item.note ?? '').trim();

  return (
    <li
      className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800"
      data-testid={`onboarding-item-${item.id}`}
      data-state={state}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{item.title}</h3>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            <ItemStatePill state={state} testId={`onboarding-item-state-${item.id}`} />
            <KindPill kind={item.kind} />
            <span className={metaCls}>{dueWords(item.due_on)}</span>
          </div>
        </div>
        {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
      </div>

      {item.description_md ? <MdLite source={item.description_md} size="sm" className="mt-2" /> : null}

      {href ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-2 inline-flex min-h-[44px] items-center gap-1 rounded text-sm font-medium text-primary-700 hover:underline focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-primary-400 sm:min-h-0"
          data-testid={`onboarding-item-link-${item.id}`}
        >
          {`Open ${hostOf(href)}`}
          <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
        </a>
      ) : null}

      <dl className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs">
        <div className="flex gap-1">
          <dt className="text-gray-500 dark:text-gray-400">Responsible:</dt>
          <dd className="font-medium text-gray-800 dark:text-gray-200" data-testid={`onboarding-responsible-${item.id}`}>
            {responsibleWords(item, viewer, hireName)}
          </dd>
        </div>
        <div className="flex gap-1">
          <dt className="text-gray-500 dark:text-gray-400">Evidence:</dt>
          <dd className="text-gray-800 dark:text-gray-200">
            {EVIDENCE_LABELS[item.evidence] ?? item.evidence}
            {item.requires_verification ? ', verified by an admin' : ''}
          </dd>
        </div>
      </dl>

      {item.status === 'done' && (evidenceText || item.has_evidence_file || item.done_at) ? (
        <div className="mt-2 space-y-1 rounded-lg bg-gray-50 px-3 py-2 text-sm dark:bg-gray-900/40" data-testid={`onboarding-item-evidence-${item.id}`}>
          {item.done_at ? <p className={metaCls}>{`Done ${formatDay(item.done_at)}`}</p> : null}
          {evidenceText ? (
            <p className="whitespace-pre-line text-gray-800 dark:text-gray-200">
              <span className="font-medium">Confirmation: </span>
              {evidenceText}
            </p>
          ) : null}
          {item.has_evidence_file ? (
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1 text-gray-800 dark:text-gray-200">
                <Paperclip className="h-3.5 w-3.5 text-gray-400" aria-hidden="true" />
                {item.filename || 'Evidence file'}
              </span>
              {onViewEvidence ? (
                <button
                  type="button"
                  onClick={onViewEvidence}
                  disabled={viewing}
                  className="inline-flex min-h-[44px] items-center gap-1 rounded text-sm font-medium text-primary-700 hover:underline focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 dark:text-primary-400 sm:min-h-0"
                  data-testid={`onboarding-item-view-${item.id}`}
                >
                  {viewing ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : null}
                  View file
                </button>
              ) : null}
            </div>
          ) : null}
          {blockedUrl ? <EvidenceBlockedLink url={blockedUrl} /> : null}
          {item.verified_at ? <p className="text-xs text-green-700 dark:text-green-400">{`Verified ${formatDay(item.verified_at)}`}</p> : null}
        </div>
      ) : null}

      {note ? (
        <p className="mt-2 whitespace-pre-line text-sm text-gray-700 dark:text-gray-300" data-testid={`onboarding-item-note-${item.id}`}>
          <span className="font-medium">{item.status === 'not_applicable' ? 'Reason: ' : 'Note: '}</span>
          {note}
        </p>
      ) : null}
    </li>
  );
}

export default ItemCard;
